import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './useAuth';
import type { Tables, TablesInsert } from '@/integrations/supabase/types';

export type Club = Tables<'clubs'>;
export type ClubMember = Tables<'club_members'>;

type SportType = 'badminton' | 'football' | 'pickleball' | 'tabletennis' | 'tennis' | 'volleyball';

export function useClubs(sport?: string) {
  return useQuery({
    queryKey: ['clubs', sport],
    queryFn: async () => {
      let query = supabase.from('clubs').select('*').order('created_at', { ascending: false });
      
      if (sport && sport !== 'all') {
        query = query.eq('sport', sport as SportType);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as Club[];
    },
  });
}

export function useClub(id: string) {
  return useQuery({
    queryKey: ['club', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clubs')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      
      if (error) throw error;
      return data as Club | null;
    },
    enabled: !!id,
  });
}

export function useClubMembers(clubId: string) {
  return useQuery({
    queryKey: ['club-members', clubId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('club_members')
        .select(`
          *,
          profiles:profile_id (
            id,
            name,
            avatar,
            rank,
            rank_points,
            sports,
            location
          )
        `)
        .eq('club_id', clubId)
        .order('joined_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!clubId,
  });
}

export function useMyClubMembership(clubId: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['my-membership', clubId, user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      // First get the profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (!profile) return null;
      
      const { data, error } = await supabase
        .from('club_members')
        .select('*')
        .eq('club_id', clubId)
        .eq('profile_id', profile.id)
        .maybeSingle();
      
      if (error) throw error;
      return data as ClubMember | null;
    },
    enabled: !!clubId && !!user,
  });
}

interface TrainingScheduleInput {
  day_of_week: number;
  start_time: string;
  end_time: string;
  location?: string;
  notes?: string;
}

interface TrainingLocationInput {
  court_id?: string | null;
  name: string;
  address?: string;
  latitude: number;
  longitude: number;
  is_primary: boolean;
}

interface CreateClubInput extends Omit<TablesInsert<'clubs'>, 'owner_id'> {
  trainingSchedules?: TrainingScheduleInput[];
  trainingLocations?: TrainingLocationInput[];
  recruitment_slots?: number;
  weekly_fee?: number;
  monthly_fee?: number;
  quarterly_fee?: number;
}

export function useCreateClub() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ trainingSchedules, trainingLocations, recruitment_slots, weekly_fee, monthly_fee, quarterly_fee, ...clubData }: CreateClubInput) => {
      if (!user) throw new Error('Bạn cần đăng nhập để tạo CLB');
      
      // Get the profile id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (profileError || !profile) throw new Error('Không tìm thấy hồ sơ người dùng');
      
      // Create the club with recruitment fields
      const { data: club, error: clubError } = await supabase
        .from('clubs')
        .insert({
          ...clubData,
          owner_id: profile.id,
          recruitment_slots: recruitment_slots || 0,
          weekly_fee: weekly_fee || 0,
          monthly_fee: monthly_fee || 0,
          quarterly_fee: quarterly_fee || 0,
        })
        .select()
        .single();
      
      if (clubError) throw clubError;
      
      // Add creator as admin member
      const { error: memberError } = await supabase
        .from('club_members')
        .insert({
          club_id: club.id,
          profile_id: profile.id,
          role: 'admin',
        });
      
      if (memberError) throw memberError;
      
      // Create training locations if provided
      if (trainingLocations && trainingLocations.length > 0) {
        const locationInserts = trainingLocations.map(loc => ({
          club_id: club.id,
          court_id: loc.court_id || null,
          name: loc.name,
          address: loc.address || null,
          latitude: loc.latitude,
          longitude: loc.longitude,
          is_primary: loc.is_primary,
        }));

        const { error: locError } = await supabase
          .from('club_training_locations')
          .insert(locationInserts);

        if (locError) {
          console.error('Error creating training locations:', locError);
        }
      }

      // Create training schedules if provided
      if (trainingSchedules && trainingSchedules.length > 0) {
        const scheduleInserts = trainingSchedules.map(schedule => ({
          club_id: club.id,
          day_of_week: schedule.day_of_week,
          start_time: schedule.start_time,
          end_time: schedule.end_time,
          location: schedule.location || null,
          notes: schedule.notes || null,
        }));

        const { error: scheduleError } = await supabase
          .from('club_training_schedules')
          .insert(scheduleInserts);

        if (scheduleError) {
          console.error('Error creating training schedules:', scheduleError);
          // Don't throw here, club is already created
        }
      }
      
      return club;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
      toast({
        title: 'Thành công',
        description: 'Đã tạo CLB mới thành công!',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Lỗi',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * @deprecated This hook is DEPRECATED and should NOT be used.
 * 
 * REASON: This bypasses the proper club join workflow which requires:
 * 1. User submits a join request via ClubJoinRequestDialog
 * 2. Club admin reviews and approves/rejects
 * 3. If approved with fee, user must complete payment
 * 4. Payment triggers automatic member addition
 * 
 * USE INSTEAD: ClubJoinRequestDialog component for proper join flow
 * 
 * This hook was kept for backward compatibility but will be removed
 * in a future version. Any usage should be replaced with the proper
 * join request workflow.
 */
export function useJoinClub() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (clubId: string) => {
      // Throw error to prevent usage of deprecated function
      throw new Error(
        'Tính năng tham gia trực tiếp đã bị vô hiệu. ' +
        'Vui lòng sử dụng nút "Xin gia nhập" để gửi đơn đăng ký.'
      );
    },
    onError: (error: Error) => {
      toast({
        title: 'Không thể tham gia',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useLeaveClub() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (clubId: string) => {
      if (!user) throw new Error('Bạn cần đăng nhập');
      
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (profileError || !profile) throw new Error('Không tìm thấy hồ sơ người dùng');

      // BUG 17 FIX: Check if user is the only admin — block leaving if so
      const { data: myMembership } = await supabase
        .from('club_members')
        .select('role')
        .eq('club_id', clubId)
        .eq('profile_id', profile.id)
        .maybeSingle();

      if (myMembership?.role === 'admin') {
        // Check if there are other admins
        const { count: adminCount } = await supabase
          .from('club_members')
          .select('id', { count: 'exact', head: true })
          .eq('club_id', clubId)
          .eq('role', 'admin');

        if (!adminCount || adminCount <= 1) {
          throw new Error('Bạn là admin duy nhất của CLB. Hãy chuyển quyền admin cho thành viên khác trước khi rời CLB.');
        }
      }

      // Also check if user is the club owner
      const { data: club } = await supabase
        .from('clubs')
        .select('owner_id')
        .eq('id', clubId)
        .maybeSingle();

      if (club?.owner_id === profile.id) {
        throw new Error('Chủ sở hữu CLB không thể rời CLB. Hãy chuyển quyền sở hữu trước.');
      }
      
      const { error } = await supabase
        .from('club_members')
        .delete()
        .eq('club_id', clubId)
        .eq('profile_id', profile.id);
      
      if (error) throw error;
    },
    onSuccess: (_, clubId) => {
      queryClient.invalidateQueries({ queryKey: ['club-members', clubId] });
      queryClient.invalidateQueries({ queryKey: ['my-membership', clubId] });
      queryClient.invalidateQueries({ queryKey: ['clubs'] });
      toast({
        title: 'Thành công',
        description: 'Đã rời khỏi CLB',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Lỗi',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
